/**
 * CommandPalette – Full-screen modal for Firmflow admin portal.
 * Trigger: Cmd+K (Mac) / Ctrl+K (Windows).
 * Sections: Navigate, Create, Search (API results when query >= 2).
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  Box,
  TextField,
  Typography,
  ListItemButton,
  InputAdornment,
  Skeleton,
  Slide,
} from '@mui/material';
import {
  Search as SearchIcon,
  Person as PersonIcon,
  Receipt as ReceiptIcon,
  InsertDriveFile as InsertDriveFileIcon,
  CheckCircle as CheckCircleIcon,
  AccountTree as AccountTreeIcon,
  Add as AddIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import api from '../api';
import { tokens } from '../theme';

const SECTION_CONFIG = [
  { key: 'clients', label: 'Clients', Icon: PersonIcon },
  { key: 'invoices', label: 'Invoices', Icon: ReceiptIcon },
  { key: 'files', label: 'Files', Icon: InsertDriveFileIcon },
  { key: 'tasks', label: 'Tasks', Icon: CheckCircleIcon },
  { key: 'workflows', label: 'Workflows', Icon: AccountTreeIcon },
];

const NAVIGATE_ACTIONS = [
  { label: 'Clients', url: '/client_list', Icon: PersonIcon, hint: 'Go to Clients' },
  { label: 'Invoices', url: '/billing/invoices', Icon: ReceiptIcon, hint: 'Go to Invoices' },
  { label: 'Files', url: '/files/clients/root', Icon: InsertDriveFileIcon, hint: 'Go to Files' },
  { label: 'Tasks', url: '/tasks', Icon: CheckCircleIcon, hint: 'Go to Tasks' },
  { label: 'Workflows', url: '/workflows', Icon: AccountTreeIcon, hint: 'Go to Workflows' },
  { label: 'Workflow templates', url: '/workflows/templates', Icon: AccountTreeIcon, hint: 'Create and edit reusable templates' },
  { label: 'Settings', url: '/settings', Icon: SettingsIcon, hint: 'Go to Settings' },
];

const CREATE_ACTIONS = [
  { label: 'New Invoice', url: '/billing/invoices/create', Icon: ReceiptIcon, hint: 'Create invoice' },
  { label: 'New Client', url: '/client_list', Icon: PersonIcon, hint: 'Go to Clients' },
  { label: 'New Task', url: '/tasks?create=1', Icon: CheckCircleIcon, hint: 'Create task' },
  { label: 'New Workflow', url: '/workflows', Icon: AccountTreeIcon, hint: 'Assign a workflow to a client' },
  { label: 'New workflow template', url: '/workflows/templates', Icon: AccountTreeIcon, hint: 'Define tasks for reusable workflows' },
];

const DEBOUNCE_MS = 300;

const TYPE_ICON_MAP = {
  client: PersonIcon,
  invoice: ReceiptIcon,
  file: InsertDriveFileIcon,
  task: CheckCircleIcon,
  workflow: AccountTreeIcon,
};

const Transition = React.forwardRef((props, ref) => (
  <Slide direction="up" ref={ref} {...props} />
));

const CommandPalette = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [highlightIndex, setHighlightIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [searchResults, setSearchResults] = useState(null);
  const inputRef = useRef(null);
  const debounceTimerRef = useRef(null);

  const fetchSearch = useCallback(async (q) => {
    setLoading(true);
    setSearchResults(null);
    try {
      const res = await api.get('/api/search/', { params: { q: q.trim() } });
      if (res.data?.success && res.data.results) {
        setSearchResults(res.data.results);
      } else {
        setSearchResults({});
      }
    } catch (err) {
      setSearchResults({});
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    const q = query.trim();
    if (q.length >= 2) {
      debounceTimerRef.current = setTimeout(() => fetchSearch(q), DEBOUNCE_MS);
      return () => {
        if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
      };
    }
    setSearchResults(null);
    setLoading(false);
  }, [query, fetchSearch]);

  const filterActions = (actions, q) => {
    if (!q) return actions;
    const lower = q.toLowerCase();
    return actions.filter((a) => a.label.toLowerCase().includes(lower));
  };

  const buildItems = useCallback(() => {
    const q = query.trim().toLowerCase();
    const navFiltered = filterActions(NAVIGATE_ACTIONS, query);
    const createFiltered = filterActions(CREATE_ACTIONS, query);
    const items = [];
    navFiltered.forEach((a) => items.push({ ...a, section: 'navigate' }));
    createFiltered.forEach((a) => items.push({ ...a, section: 'create' }));
    if (searchResults && q.length >= 2) {
      SECTION_CONFIG.forEach(({ key }) => {
        const arr = searchResults[key];
        if (Array.isArray(arr)) {
          arr.slice(0, 5).forEach((item) => items.push({ ...item, section: 'search' }));
        }
      });
    }
    return items;
  }, [query, searchResults]);

  const items = buildItems();

  useEffect(() => {
    setHighlightIndex((i) => Math.min(i, Math.max(0, items.length - 1)));
  }, [items.length]);

  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setOpen((prev) => {
          const next = !prev;
          if (next) {
            setQuery('');
            setHighlightIndex(0);
            setTimeout(() => inputRef.current?.focus(), 100);
          }
          return next;
        });
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  const handleClose = () => {
    setOpen(false);
    setQuery('');
    setSearchResults(null);
  };

  const normalizeUrl = (url) => {
    if (!url || typeof url !== 'string') return url;
    let u = url.replace(/^\/clients\//, '/client/');
    if (/^\/workflows\/\d+$/.test(u)) u = '/workflows';
    return u;
  };

  const handleExecute = (item) => {
    const url = item.url ? normalizeUrl(item.url) : null;
    if (url) {
      navigate(url);
      handleClose();
    }
  };

  const handleKeyDown = (e) => {
    if (!open) return;
    if (e.key === 'Escape') {
      handleClose();
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightIndex((i) => Math.min(i + 1, Math.max(0, items.length - 1)));
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIndex((i) => Math.max(0, i - 1));
      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      const item = items[highlightIndex];
      if (item) handleExecute(item);
    }
  };

  const hasNavFiltered = filterActions(NAVIGATE_ACTIONS, query).length > 0;
  const hasCreateFiltered = filterActions(CREATE_ACTIONS, query).length > 0;

  const flatSearchItems = [];
  if (searchResults && query.trim().length >= 2) {
    SECTION_CONFIG.forEach(({ key }) => {
      const arr = searchResults[key];
      if (Array.isArray(arr)) arr.slice(0, 5).forEach((item) => flatSearchItems.push(item));
    });
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={false}
      TransitionComponent={Transition}
      PaperProps={{
        sx: {
          width: '100%',
          minWidth: tokens.commandPaletteMinWidth,
          maxWidth: tokens.commandPaletteMaxWidth,
          borderRadius: 2,
          maxHeight: '80vh',
          overflow: 'hidden',
          border: '1px solid',
          borderColor: 'divider',
        },
      }}
    >
      <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
        <TextField
          inputRef={inputRef}
          size="small"
          fullWidth
          placeholder="Search or type a command..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: 'action.active' }} />
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              bgcolor: 'background.paper',
              '& fieldset': { borderColor: 'divider' },
            },
          }}
        />
      </Box>
      <Box sx={{ overflow: 'auto', maxHeight: 360, py: 1 }}>
        {loading && (
          <Box sx={{ p: 2 }}>
            {[1, 2, 3, 4].map((i) => (
              <Skeleton key={i} height={48} sx={{ mb: 0.5 }} />
            ))}
          </Box>
        )}
        {!loading && (
          <>
            {hasNavFiltered && (
              <Box sx={{ mb: 1 }}>
                <Typography
                  variant="overline"
                  sx={{
                    px: 2,
                    display: 'block',
                    mb: 0.5,
                    color: 'text.secondary',
                    fontWeight: 600,
                    letterSpacing: 0.5,
                    fontSize: 11,
                    textTransform: 'uppercase',
                  }}
                >
                  Navigate
                </Typography>
                {filterActions(NAVIGATE_ACTIONS, query).map((action) => {
                  const Icon = action.Icon;
                  const flatIdx = items.findIndex((x) => x.section === 'navigate' && x.label === action.label);
                  return (
                    <ListItemButton
                      key={`nav-${action.label}`}
                      selected={highlightIndex === flatIdx}
                      onClick={() => handleExecute(action)}
                      sx={{
                        py: 1,
                        '&.Mui-selected': { bgcolor: 'action.selected' },
                        '&:hover': { bgcolor: 'action.hover' },
                      }}
                    >
                      <Icon sx={{ mr: 1.5, color: 'action.active' }} fontSize="small" />
                      <Typography variant="body2">{action.label}</Typography>
                      {action.hint && (
                        <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto', pl: 1 }}>
                          {action.hint}
                        </Typography>
                      )}
                    </ListItemButton>
                  );
                })}
              </Box>
            )}
            {hasCreateFiltered && (
              <Box sx={{ mb: 1 }}>
                <Typography
                  variant="overline"
                  sx={{
                    px: 2,
                    display: 'block',
                    mb: 0.5,
                    color: 'text.secondary',
                    fontWeight: 600,
                    letterSpacing: 0.5,
                    fontSize: 11,
                    textTransform: 'uppercase',
                  }}
                >
                  Create
                </Typography>
                {filterActions(CREATE_ACTIONS, query).map((action) => {
                  const flatIdx = items.findIndex((x) => x.section === 'create' && x.label === action.label);
                  return (
                    <ListItemButton
                      key={`create-${action.label}`}
                      selected={highlightIndex === flatIdx}
                      onClick={() => handleExecute(action)}
                      sx={{
                        py: 1,
                        '&.Mui-selected': { bgcolor: 'action.selected' },
                        '&:hover': { bgcolor: 'action.hover' },
                      }}
                    >
                      <AddIcon sx={{ mr: 1.5, color: 'action.active' }} fontSize="small" />
                      <Typography variant="body2">{action.label}</Typography>
                      {action.hint && (
                        <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto', pl: 1 }}>
                          {action.hint}
                        </Typography>
                      )}
                    </ListItemButton>
                  );
                })}
              </Box>
            )}
            {query.trim().length >= 2 && flatSearchItems.length > 0 && (
              <Box sx={{ mb: 1 }}>
                <Typography
                  variant="overline"
                  sx={{
                    px: 2,
                    display: 'block',
                    mb: 0.5,
                    color: 'text.secondary',
                    fontWeight: 600,
                    letterSpacing: 0.5,
                    fontSize: 11,
                    textTransform: 'uppercase',
                  }}
                >
                  Search
                </Typography>
                {flatSearchItems.map((item) => {
                  const Icon = TYPE_ICON_MAP[item.type] || SearchIcon;
                  const flatIdx = items.findIndex((x) => x.section === 'search' && x.id === item.id && x.type === item.type);
                  return (
                    <ListItemButton
                      key={`search-${item.type}-${item.id}`}
                      selected={highlightIndex === flatIdx}
                      onClick={() => handleExecute(item)}
                      sx={{
                        py: 1,
                        '&.Mui-selected': { bgcolor: 'action.selected' },
                        '&:hover': { bgcolor: 'action.hover' },
                      }}
                    >
                      <Icon sx={{ mr: 1.5, color: 'action.active' }} fontSize="small" />
                      <Box>
                        <Typography variant="body2">{item.label}</Typography>
                        {item.sublabel && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            {item.sublabel}
                          </Typography>
                        )}
                      </Box>
                    </ListItemButton>
                  );
                })}
              </Box>
            )}
            {!loading && items.length === 0 && (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  No results found
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  Try a different search term or command
                </Typography>
              </Box>
            )}
          </>
        )}
      </Box>
    </Dialog>
  );
};

export default CommandPalette;
